import { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { userManager, isRedirectCallback, cleanCallbackUrl } from '../lib/oidcConfig';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Deux jetons cohabitent, volontairement :
 *
 *  - l'access token Keycloak, qui prouve *qui* est l'utilisateur. Il ne sert
 *    qu'une fois, à l'ouverture de session ;
 *  - le jeton applicatif émis par l'API, présenté à chaque appel ensuite.
 *
 * Le second n'est pas une redondance : il porte le rôle déjà résolu et évite
 * que chaque requête aille revalider un token auprès de Keycloak.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('devops_token'));
  const [loading, setLoading] = useState(true);
  const initialised = useRef(false);

  useEffect(() => {
    // React monte deux fois les effets en développement ; sans ce garde-fou le
    // code d'autorisation serait consommé deux fois et le second échange
    // échouerait avec une erreur incompréhensible.
    if (initialised.current) return;
    initialised.current = true;

    const init = async () => {
      try {
        if (isRedirectCallback()) {
          const oidcUser = await userManager.signinRedirectCallback();
          // Nettoyer l'URL avant l'échange : si celui-ci échoue, un
          // rechargement ne doit pas rejouer un code déjà consommé.
          cleanCallbackUrl();
          const { data } = await axios.post(`${API_URL}/api/auth/oidc`, {
            access_token: oidcUser.access_token,
          });
          localStorage.setItem('devops_token', data.access_token);
          setToken(data.access_token);
          setUser(data.user);
          setLoading(false);
          return;
        }

        const stored = localStorage.getItem('devops_token');
        if (stored) {
          const { data } = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${stored}` },
          });
          setToken(stored);
          setUser(data);
        }
      } catch (error) {
        console.error('[Auth] initialisation :', error);
        cleanCallbackUrl();
        localStorage.removeItem('devops_token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const login = async () => {
    // La page navigue vers Keycloak, qui redirige lui-même vers Microsoft.
    await userManager.signinRedirect();
  };

  const logout = async () => {
    localStorage.removeItem('devops_token');
    setToken(null);
    setUser(null);
    try {
      // Ferme aussi la session Keycloak, sinon un nouveau « se connecter »
      // reconnecterait silencieusement sans repasser par l'écran de connexion.
      await userManager.signoutRedirect();
    } catch {
      window.location.href = '/';
    }
  };

  const getAuthHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      getAuthHeader,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
